import { FC } from 'react';
import {
  Text,
  Card,
  VStack,
  HStack,
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  useDisclosure,
} from '@wegroup/design-system';
import { AnvaQuote } from '@flows/src/types/anva';
import { toEuro } from '@sales-conversation/src/utils/stringUtils';
import useFlowsProductBasketStore from '@flows/src/store/zustand/flows-products/flowsProductBasketStore';
import { useTranslation } from 'react-i18next';
import AnvaAddToPackageModal from '@flows/src/components/flow-products/quotes/basket/AnvaAddToPackageModal';
import cogoToast from 'cogo-toast';

interface Props {
  data: AnvaQuote;
}

const FlowProductsSideBarBasketItem: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Import necessary functions from the basket store
  const addToInsurances = useFlowsProductBasketStore(
    (state) => state.addToInsurances,
  );
  const addToPackage = useFlowsProductBasketStore(
    (state) => state.addToPackage,
  );
  const createPackage = useFlowsProductBasketStore(
    (state) => state.createPackage,
  );

  // Handler for adding as package
  const handleAddToPackage = (packageCode: string) => {
    // Create package if it doesn't exist yet, then add quote to it
    const anvaPackage = data.possible_packages.find(
      (pkg) => pkg.code === packageCode,
    );
    if (!anvaPackage) return;
    createPackage(anvaPackage);
    addToPackage(packageCode, data);

    cogoToast.success(t('FLOWS_PRODUCT_ADDED_TO_PACKAGE'));
  };

  // Handler for adding as loose insurance
  const handleAddToInsurance = () => {
    addToInsurances(data);

    cogoToast.success(t('FLOWS_PRODUCT_ADDED_AS_INSURANCE'));
  };

  return (
    <>
      <AnvaAddToPackageModal
        isOpen={isOpen}
        onClose={onClose}
        packageOptions={data.possible_packages}
        onAddToPackage={handleAddToPackage}
      />
      <Card boxShadow="none">
        <HStack justify="flex-start">
          <VStack align="stretch" spacing="0" flexGrow={1}>
            <Text textStyle="smallBody" color="gray.300">
              {data.insurance_company.name}
            </Text>
            <Text textStyle="normalBodyMedium">{data.insurance.name}</Text>
          </VStack>
          <Text color="gray.500">{toEuro(data?.total_premium)}</Text>
        </HStack>
        <Menu placement="right-start">
          <MenuButton
            as={Button}
            ml="auto"
            mt="2"
            size="sm"
            variant="secondary"
            rightIcon={<Icon name="GeneralActionsChevronRight" boxSize="5" />}
          >
            Verzekeren als
          </MenuButton>
          <Portal>
            <MenuList>
              <MenuItem
                icon={<Icon name="GeneralOthersBox" />}
                sx={{ svg: { scale: 0.9, transform: 'auto' } }}
                onClick={onOpen}
              >
                Pakket
              </MenuItem>
              <MenuItem
                icon={<Icon name="GeneralNavigationShoppingBag" />}
                onClick={handleAddToInsurance}
              >
                Losse verzekering
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      </Card>
    </>
  );
};

export default FlowProductsSideBarBasketItem;
